import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { Pencil, Trash2, UserPlus } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"
import { toast } from "sonner"
import { useEntitlements } from "@/hooks/use-entitlements"

const API = API_BASE

const STATUSES = ["pending", "accepted", "in_treatment", "completed", "cancelled"]

const emptyForm = () => ({
  client_id: "",
  source_name: "",
  source_facility: "",
  source_physician: "",
  status: "pending",
  notes: "",
})

function statusBadgeVariant(status) {
  if (status === "completed") return "outline"
  if (status === "cancelled") return "destructive"
  if (status === "accepted" || status === "in_treatment") return "default"
  return "secondary"
}

export default function Referrals() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const [referrals, setReferrals] = useState([])
  const [summary, setSummary] = useState({})
  const [clients, setClients] = useState([])
  const [facilities, setFacilities] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState("")
  const [form, setForm] = useState(emptyForm())
  const [editingId, setEditingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = filterStatus ? { status: filterStatus } : {}
      const { data } = await axios.get(`${API}/referrals`, { params })
      if (Array.isArray(data)) {
        setReferrals(data)
        setSummary({ total: data.length })
      } else {
        setReferrals(data.referrals || [])
        setSummary(data.summary || {})
      }
    } catch {
      toast.error(t("app.referrals.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [filterStatus, t])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => {})
    if (!hasFeature("multi_location")) {
      setFacilities([])
      return
    }
    axios
      .get(`${API}/network/organization`)
      .then((r) => setFacilities(r.data?.facilities || []))
      .catch(() => setFacilities([]))
  }, [hasFeature])

  const resetForm = () => {
    setForm(emptyForm())
    setEditingId(null)
  }

  const save = async () => {
    if (!form.source_name.trim()) return toast.error(t("app.referrals.toast.sourceRequired"))
    const body = {
      client_id: form.client_id || null,
      source_name: form.source_name.trim(),
      source_facility: form.source_facility.trim(),
      source_physician: form.source_physician.trim(),
      status: form.status,
      notes: form.notes.trim(),
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/referrals/${editingId}`, body)
        toast.success(t("app.referrals.toast.updated"))
      } else {
        await axios.post(`${API}/referrals`, body)
        toast.success(t("app.referrals.toast.created"))
      }
      resetForm()
      load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.referrals.toast.saveFailed"))
    }
  }

  const startEdit = (r) => {
    setEditingId(r.id)
    setForm({
      client_id: r.client_id || "",
      source_name: r.source_name || "",
      source_facility: r.source_facility || "",
      source_physician: r.source_physician || "",
      status: r.status || "pending",
      notes: r.notes || "",
    })
  }

  const quickStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/referrals/${id}`, { status })
      load()
    } catch {
      toast.error(t("app.referrals.toast.saveFailed"))
    }
  }

  const deleteReferral = async (id) => {
    try {
      await axios.delete(`${API}/referrals/${id}`)
      if (editingId === id) resetForm()
      toast.success(t("app.referrals.toast.deleted"))
      load()
    } catch {
      toast.error(t("app.referrals.toast.saveFailed"))
    }
  }

  const statusLabel = (s) => t(`app.referrals.status.${s}`, { defaultValue: s })

  return (
    <FeatureGate
      feature="referrals"
      title={t("app.referrals.gateTitle")}
      description={t("app.referrals.gateDescription")}
    >
      <AppLayout title={t("app.referrals.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.referrals.intro")}{" "}
          <Link to="/app/clients" className="underline underline-offset-2">
            {t("app.nav.clients")}
          </Link>
          {" · "}
          <Link to="/app/network" className="underline underline-offset-2">
            {t("app.nav.network")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.referrals.pilotHint")}</PilotBanner>

        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            size="sm"
            variant={filterStatus === "" ? "default" : "outline"}
            className="rounded-full h-8"
            onClick={() => setFilterStatus("")}
          >
            {t("app.referrals.filterAll")} ({summary.total || 0})
          </Button>
          {STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filterStatus === s ? "default" : "outline"}
              className="rounded-full h-8"
              onClick={() => setFilterStatus(s)}
            >
              {statusLabel(s)} ({summary[s] || 0})
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <section className="rounded-xl border border-border p-6 bg-card space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              {editingId ? t("app.referrals.editReferral") : t("app.referrals.newReferral")}
            </h3>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldClient")}</Label>
              <Select
                value={form.client_id || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, client_id: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("app.referrals.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("app.referrals.noClientYet")}</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldSource")}</Label>
              <Input
                className="mt-1"
                placeholder={t("app.referrals.sourcePlaceholder")}
                value={form.source_name}
                onChange={(e) => setForm((f) => ({ ...f, source_name: e.target.value }))}
              />
            </div>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldFacility")}</Label>
              {facilities.length > 0 ? (
                <Select
                  value={
                    facilities.some((fac) => fac.name === form.source_facility)
                      ? form.source_facility
                      : "__custom__"
                  }
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      source_facility: v === "__custom__" ? f.source_facility : v,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__custom__">{t("app.referrals.customFacility")}</SelectItem>
                    {facilities.map((fac) => (
                      <SelectItem key={fac.id} value={fac.name}>
                        {fac.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Input
                className="mt-1"
                placeholder={t("app.referrals.facilityPlaceholder")}
                value={form.source_facility}
                onChange={(e) => setForm((f) => ({ ...f, source_facility: e.target.value }))}
              />
            </div>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldPhysician")}</Label>
              <Input
                className="mt-1"
                value={form.source_physician}
                onChange={(e) => setForm((f) => ({ ...f, source_physician: e.target.value }))}
              />
            </div>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldStatus")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {statusLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">{t("app.referrals.fieldNotes")}</Label>
              <Textarea
                className="mt-1 min-h-[72px]"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full" onClick={save}>
                {editingId ? t("app.referrals.save") : t("app.referrals.createBtn")}
              </Button>
              {editingId && (
                <Button variant="ghost" className="rounded-full" onClick={resetForm}>
                  {t("app.referrals.cancel")}
                </Button>
              )}
            </div>
          </section>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("app.referrals.listTitle")}</h3>
            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm max-h-[560px] overflow-auto">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.referrals.loading")}</li>
              )}
              {!loading &&
                referrals.map((r) => (
                  <li key={r.id} className="px-4 py-3 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <button
                        type="button"
                        className="text-left flex-1 min-w-0 hover:opacity-80"
                        onClick={() => startEdit(r)}
                      >
                        <p className="font-medium">{r.source_name}</p>
                        {r.client_name && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t("app.referrals.patient")}: {r.client_name}
                          </p>
                        )}
                        {(r.source_facility || r.source_physician) && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {[r.source_facility, r.source_physician].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">{formatDate(r.created_at)}</p>
                      </button>
                      <Badge variant={statusBadgeVariant(r.status)} className="shrink-0 capitalize">
                        {statusLabel(r.status)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {r.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-7 text-xs"
                          onClick={() => quickStatus(r.id, "accepted")}
                        >
                          {t("app.referrals.actionAccept")}
                        </Button>
                      )}
                      {r.status === "accepted" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-7 text-xs"
                          onClick={() => quickStatus(r.id, "in_treatment")}
                        >
                          {t("app.referrals.actionStart")}
                        </Button>
                      )}
                      {r.status === "in_treatment" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-7 text-xs"
                          onClick={() => quickStatus(r.id, "completed")}
                        >
                          {t("app.referrals.actionComplete")}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => startEdit(r)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("app.referrals.deleteTitle")}</AlertDialogTitle>
                            <AlertDialogDescription>{t("app.referrals.deleteBody")}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("app.referrals.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteReferral(r.id)}>
                              {t("app.referrals.confirmDelete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              {!loading && !referrals.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.referrals.empty")}</li>
              )}
            </ul>
          </div>
        </div>
      </AppLayout>
    </FeatureGate>
  )
}
